import * as yup from "yup";

export type IFormData = {
  legalEntityType: string;
  equityType: string;
  country: string;
  classification: string;
  businessSectorId: string;
  logo: string;
  businessName: string;
  businessNameEng: string;
  stateRegNum: string;
  stateRegDate: string;
  regNumber: string;
  isVatPayer: boolean | null;
  web: string;
  email: string;
  email2: string;
  phone: string;
  phone2: string;
  fbLink: string;
  province: string;
  district: string;
  khoroo: string;
  khotkhonBair: string;
  khoroolol: string;
  khashaaDavkhar: string;
  khaalgaDugaar: string;
  businessAddress: string;
  locationLat: string;
  locationLng: string;
  location: any;
  partnerType: string;
};

export const buildSchema = (t: (k: string) => string) =>
  yup.object({
    legalEntityType: yup.string().when(["partnerType"], ([type]) =>
      type === "CITIZEN"
        ? yup.string().nullable()
        : yup.string().typeError(t("common:required")).required(t("common:required")),
    ),
    equityType: yup.string().when(["partnerType"], ([type]) =>
      type === "CITIZEN"
        ? yup.string().nullable()
        : yup.string().typeError(t("common:required")).required(t("common:required")),
    ),
    country: yup.string().typeError(t("common:required")).required(t("common:required")),
    // CITIZEN uses business sector instead of the corporate/SME classification,
    // so classification is only required for companies (and its field is hidden
    // for citizens — required validation there would block an invisible field).
    classification: yup.string().when(["partnerType"], ([type]) =>
      type === "CITIZEN"
        ? yup.string().nullable()
        : yup.string().required(t("common:required")),
    ),
    // CITIZEN: "Үйл ажиллагааны чиглэл" = business sector (required); COMPANY keeps classification.
    businessSectorId: yup.string().when(["partnerType"], ([type]) =>
      type === "CITIZEN"
        ? yup.string().required(t("common:required"))
        : yup.string().nullable(),
    ),
    logo: yup.string().nullable(),
    businessName: yup
      .string()
      .matches(/^[А-Яа-я|Үү|Өө|Ёё\s-.]{2,256}$/, t("common:validation.cyrillicOnly"))
      .required(t("common:required")),
    businessNameEng: yup
      .string()
      .matches(/^[A-Za-z\s-.]{2,256}$/, {
        message: t("common:validation.latinOnly"),
        excludeEmptyString: true,
      })
      .nullable(),
    regNumber: yup.string().when(["partnerType"], ([type]) => {
      if (type === "CITIZEN") {
        return yup
          .string()
          .required(t("common:required"))
          .matches(/^[А-Яа-я|Үү|Өө|Ёё]{2}\d{8}$/, t("common:validation.invalidFormat"))
          .min(10, t("common:validation.invalidRegNum"));
      } else if (type === "COMPANY") {
        return yup
          .string()
          .required(t("common:required"))
          .matches(/^\d{7}$/, t("common:validation.invalidFormat"))
          .min(7, t("common:validation.regNum7digit"))
          .max(7, t("common:validation.regNum7digit"));
      }
      return yup.string().nullable();
    }),
    web: yup
      .string()
      .transform((value) => (value ? value : null))
      .matches(
        /^((ftp|http|https):\/\/)?(www.)?(?!.*(ftp|http|https|www.))[a-zA-Z0-9_-]+(\.[a-zA-Z]+)+((\/)[\w#]+)*(\/\w+\?[a-zA-Z0-9_]+=\w+(&[a-zA-Z0-9_]+=\w+)*)?$/gm,
        t("common:validation.invalidWeb"),
      )
      .nullable(),
    email: yup.string().email(t("common:validation.invalidEmail")).required(t("common:required")),
    email2: yup
      .string()
      .transform((value) => (value ? value : null))
      .email(t("common:validation.invalidEmail"))
      .nullable(),
    phone: yup
      .string()
      .matches(/^[9|8|6]{1}[0-9]{7}$/, t("common:validation.phoneOnly"))
      .typeError(t("common:validation.phoneOnly"))
      .required(t("common:required")),
    phone2: yup
      .string()
      .transform((value) => (value ? value : null))
      .matches(/^[9|8|6]{1}[0-9]{7}$/, t("common:validation.phoneOnly"))
      .nullable(),
    fbLink: yup
      .string()
      .transform((value) => (value ? value.trim() : null))
      .matches(/^https?:\/\/(www\.)?facebook\.com\/[a-zA-Z0-9._-]+\/?$/i, t("common:validation.invalidFacebook"))
      .nullable(),
    province: yup.string().typeError(t("common:required")).required(t("common:required")),
    district: yup.string().typeError(t("common:required")).required(t("common:required")),
    khoroo: yup.string().typeError(t("common:required")).required(t("common:required")),
    khotkhonBair: yup.string().required(t("common:required")),
    khoroolol: yup.string().required(t("common:required")),
    khashaaDavkhar: yup.string().required(t("common:required")),
    khaalgaDugaar: yup.string().required(t("common:required")),
    businessAddress: yup.string().required(t("common:required")),
    locationLat: yup
      .number()
      .transform((value, original) => (original === "" || original === null ? null : value))
      .typeError(t("common:validation.numberOnly"))
      .nullable(),
    locationLng: yup
      .number()
      .transform((value, original) => (original === "" || original === null ? null : value))
      .typeError(t("common:validation.numberOnly"))
      .nullable(),
    partnerType: yup.string().nullable(),
  });

export type SectionId = "identity" | "contact" | "address";

export const SECTION_IDS: Record<SectionId, string> = {
  identity: "partner-section-identity",
  contact: "partner-section-contact",
  address: "partner-section-address",
};

/** The fields each section owns; a section save overlays only these on the partner's current values. */
export const SECTION_FIELDS: Record<SectionId, (keyof IFormData)[]> = {
  identity: [
    "regNumber",
    "businessName",
    "businessNameEng",
    "legalEntityType",
    "equityType",
    "country",
    "classification",
    "businessSectorId",
  ],
  contact: ["phone", "phone2", "email", "email2", "web", "fbLink"],
  address: [
    "province",
    "district",
    "khoroo",
    "khoroolol",
    "khotkhonBair",
    "khashaaDavkhar",
    "khaalgaDugaar",
    "businessAddress",
    "locationLat",
    "locationLng",
  ],
};

/** Locked in the contract: shown with a lock and never an input. regNumber unlocks until the partner is verified. */
export function lockedFields(regNumberLocked: boolean): (keyof IFormData)[] {
  return regNumberLocked ? ["businessName", "phone", "email", "regNumber"] : ["businessName", "phone", "email"];
}

export function toFormValues(partner: any): IFormData {
  return {
    legalEntityType: partner?.legalEntityType ?? "",
    equityType: partner?.equityType ?? "",
    country: partner?.country ?? "",
    classification: partner?.classification ?? "",
    businessSectorId: partner?.businessSectorId ?? "",
    logo: partner?.logo ?? "",
    businessName: partner?.businessName ?? "",
    businessNameEng: partner?.businessNameEng ?? "",
    stateRegNum: partner?.stateRegNum ?? "",
    stateRegDate: partner?.stateRegDate ?? "",
    regNumber: partner?.regNumber ?? "",
    isVatPayer: partner?.isVatPayer ?? null,
    web: partner?.web ?? "",
    email: partner?.email ?? "",
    email2: partner?.email2 ?? "",
    phone: partner?.phone ?? "",
    phone2: partner?.phone2 ?? "",
    fbLink: partner?.fbLink ?? "",
    province: partner?.province ?? "",
    district: partner?.district ?? "",
    khoroo: partner?.khoroo ?? "",
    khotkhonBair: partner?.khotkhonBair ?? "",
    khoroolol: partner?.khoroolol ?? "",
    khashaaDavkhar: partner?.khashaaDavkhar ?? "",
    khaalgaDugaar: partner?.khaalgaDugaar ?? "",
    businessAddress: partner?.businessAddress ?? "",
    locationLat: partner?.locationLat ?? "",
    locationLng: partner?.locationLng ?? "",
    location: partner?.location ?? null,
    partnerType: partner?.type ?? "",
  };
}

/**
 * The first section, other than the ones being saved, whose required fields the merged body lacks.
 * PUT /prt/partner needs the whole body, so a gap elsewhere would make the backend answer 400.
 */
export function findIncompleteSection(
  schema: ReturnType<typeof buildSchema>,
  merged: IFormData,
  current: SectionId[],
): SectionId | null {
  try {
    schema.validateSync(merged, { abortEarly: false });
    return null;
  } catch (error) {
    const paths = ((error as yup.ValidationError).inner ?? []).map((item) => item.path ?? "");
    const order: SectionId[] = ["identity", "contact", "address"];
    return order.find((id) => !current.includes(id) && SECTION_FIELDS[id].some((key) => paths.includes(key))) ?? null;
  }
}

const asText = (v: unknown) => (v === null || v === undefined ? "" : String(v));

/** True when any field of the section differs from the partner's stored value. */
export function isSectionDirty(values: IFormData, baseline: IFormData, section: SectionId): boolean {
  return SECTION_FIELDS[section].some((key) => asText(values[key]) !== asText(baseline[key]));
}

/** The partner's current values with the given sections' form values laid over them. */
export function overlaySections(baseline: IFormData, values: IFormData, sections: SectionId[]): IFormData {
  const merged: IFormData = { ...baseline };
  for (const section of sections) {
    for (const key of SECTION_FIELDS[section]) {
      (merged as Record<string, unknown>)[key] = values[key];
    }
  }
  return merged;
}

const nullIfEmpty = <T,>(v: T | "" | null | undefined): T | null =>
  v === "" || v === undefined || v === null ? null : (v as T);

/** PUT /prt/partner needs the WHOLE body (Joi marks most fields required), never just one section. */
export function buildPayload(values: IFormData) {
  const isCitizen = values.partnerType === "CITIZEN";
  const data: Record<string, any> = {
    legalEntityType: isCitizen ? null : values.legalEntityType,
    equityType: isCitizen ? null : values.equityType,
    country: values.country,
    classification: values.classification,
    logo: nullIfEmpty(values.logo),
    businessName: values.businessName,
    businessNameEng: values.businessNameEng,
    stateRegNum: values.stateRegNum,
    regNumber: values.regNumber,
    web: nullIfEmpty(values.web),
    email: values.email,
    email2: nullIfEmpty(values.email2),
    phone: values.phone,
    phone2: nullIfEmpty(values.phone2),
    fbLink: nullIfEmpty(values.fbLink),
    province: values.province,
    district: values.district,
    khoroo: values.khoroo,
    khotkhonBair: values.khotkhonBair,
    khoroolol: values.khoroolol,
    khashaaDavkhar: values.khashaaDavkhar,
    khaalgaDugaar: values.khaalgaDugaar,
    businessAddress: values.businessAddress,
    locationLat: nullIfEmpty(values.locationLat),
    locationLng: nullIfEmpty(values.locationLng),
    location: values.location,
    partnerType: values.partnerType,
  };
  // Only citizens edit the business sector here; omit for companies so the
  // backend never wipes an existing sector (it cascades only when sent).
  if (isCitizen) data.businessSectorId = nullIfEmpty(values.businessSectorId);
  return data;
}

export type LatLngValue = { lat: number; lng: number };

/** Ulaanbaatar centre, shown while no coordinate is stored. */
export const DEFAULT_MAP_CENTER: LatLngValue = { lat: 47.9192, lng: 106.9172 };

export function parseCoordinate(value: string | number | null | undefined): number | null {
  const parsed = typeof value === "number" ? value : parseFloat(value ?? "");
  return Number.isFinite(parsed) && parsed !== 0 ? parsed : null;
}
